/**
 * iter-386u — Dashboard "Create & Share Link" quick action.
 *
 * Modal with two tabs — Vendor / Client:
 *   • Vendor tab: pick a Project + Vendor → POST /share-links/vendor-quote
 *                 (reuses existing pending link if any).
 *   • Client tab: pick a Project + Client → POST /share-links/client-status
 *                 (creates a public project payment/ledger status page).
 * Once generated, offers Copy Link + Email via Resend.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Mail, Store, User as UserIcon, Link2, CheckCircle2, RefreshCcw } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import SearchableCombobox from '@/components/SearchableCombobox';

export default function CreateShareLinkDialog({ open, onOpenChange }) {
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [mode, setMode] = useState('vendor'); // vendor | client
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [users, setUsers] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { public_url, reused, expiry_date, ... }
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // Reset when dialog re-opens.
  useEffect(() => {
    if (open) {
      setResult(null);
      setProjectId('');
      setPartyId('');
      setEmailTo('');
    }
  }, [open, mode]);

  // Load projects + users on open.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects?archived=all&limit=100`, authHeaders);
        setProjects(r.data || []);
      } catch { /* silent */ }
      try {
        const u = await axios.get(`${API}/users`, authHeaders);
        setUsers(u.data || []);
      } catch { /* silent */ }
    })();
  }, [open]);

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name || 'Untitled Project' })),
    [projects],
  );

  const partyOptions = useMemo(() => {
    const role = mode === 'vendor' ? 'vendor' : 'client';
    return users
      .filter((u) => u.role === role)
      .map((u) => ({ value: u.id, label: `${u.name || u.email}${u.email ? ` — ${u.email}` : ''}` }));
  }, [users, mode]);

  const selectedProject = projects.find((p) => p.id === projectId);
  const selectedParty = users.find((u) => u.id === partyId);

  // Prefill emailTo when the party changes and offer a sensible default.
  useEffect(() => {
    if (selectedParty?.email) setEmailTo(selectedParty.email);
  }, [selectedParty]);

  const handleGenerate = async () => {
    if (!projectId) { toast.error('Please pick a project'); return; }
    if (!partyId && mode === 'vendor') { toast.error('Please pick a vendor'); return; }
    if (!partyId && mode === 'client') {
      // Client can be optional — fall back to project.client_id if present.
      if (!selectedProject?.client_id) { toast.error('Please pick a client (project has no client attached)'); return; }
    }
    setGenerating(true);
    setResult(null);
    try {
      if (mode === 'vendor') {
        const body = {
          project_id: projectId,
          vendor_id: partyId,
          vendor_name: selectedParty?.name || selectedParty?.email || 'Vendor',
          vendor_email: selectedParty?.email || null,
        };
        const r = await axios.post(`${API}/share-links/vendor-quote`, body, authHeaders);
        setResult({ ...r.data, kind: 'vendor' });
        toast.success(r.data.reused ? 'Reused existing quote link' : 'Vendor quote link created');
      } else {
        const body = {
          project_id: projectId,
          client_id: partyId || selectedProject?.client_id,
          client_name: selectedParty?.name || selectedProject?.client_name || null,
          client_email: selectedParty?.email || selectedProject?.client_email || null,
        };
        const r = await axios.post(`${API}/share-links/client-status`, body, authHeaders);
        setResult({ ...r.data, kind: 'client' });
        toast.success(r.data.reused ? 'Reused existing status link' : 'Client status link created');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.public_url) return;
    try {
      await navigator.clipboard.writeText(result.public_url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const handleEmail = async () => {
    if (!result?.public_url) return;
    if (!emailTo || !/.+@.+\..+/.test(emailTo)) { toast.error('Enter a valid recipient email'); return; }
    setEmailSending(true);
    try {
      const projectName = selectedProject?.name || 'the project';
      const isVendor = result.kind === 'vendor';
      const subject = isVendor
        ? `Quote request — ${projectName}`
        : `Project status link — ${projectName}`;
      const bodyHtml = isVendor
        ? `<p>Hello ${selectedParty?.name || ''},</p><p>Please review the scope and submit your best rates via the link below. This is a public link — no login required.</p>`
        : `<p>Hello ${selectedParty?.name || selectedProject?.client_name || ''},</p><p>Here is a link to view the live project status, payments received, and outstanding balance for <strong>${projectName}</strong>. No login required.</p>`;
      await axios.post(
        `${API}/share-links/email`,
        {
          to_email: emailTo,
          subject,
          body_html: bodyHtml,
          url: result.public_url,
          link_type: isVendor ? 'vendor-quote' : 'client-status',
        },
        authHeaders,
      );
      toast.success(`Email sent to ${emailTo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 size={18} className="text-amber-600" />
            Create &amp; Share Link
          </DialogTitle>
          <DialogDescription className="text-stone-500 text-sm">
            Pick a project + counterparty, then copy or email the public link. Reuses an active link if one exists.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => { setMode(v); setResult(null); setPartyId(''); }} className="w-full">
          <TabsList className="grid grid-cols-2 w-full mb-2">
            <TabsTrigger value="vendor" data-testid="share-link-mode-vendor">
              <Store size={14} className="mr-1.5" /> Vendor
            </TabsTrigger>
            <TabsTrigger value="client" data-testid="share-link-mode-client">
              <UserIcon size={14} className="mr-1.5" /> Client
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vendor" className="mt-0 space-y-3">
            <div>
              <Label className="text-xs">Project *</Label>
              <SearchableCombobox
                options={projectOptions}
                value={projectId}
                onValueChange={setProjectId}
                placeholder="Select project"
                searchPlaceholder="Search projects..."
                emptyText="No projects found"
                data-testid="share-link-project-vendor"
              />
            </div>
            <div>
              <Label className="text-xs">Vendor *</Label>
              <SearchableCombobox
                options={partyOptions}
                value={partyId}
                onValueChange={setPartyId}
                placeholder="Select vendor"
                searchPlaceholder="Search vendors..."
                emptyText="No vendors found"
                data-testid="share-link-vendor"
              />
            </div>
          </TabsContent>

          <TabsContent value="client" className="mt-0 space-y-3">
            <div>
              <Label className="text-xs">Project *</Label>
              <SearchableCombobox
                options={projectOptions}
                value={projectId}
                onValueChange={setProjectId}
                placeholder="Select project"
                searchPlaceholder="Search projects..."
                emptyText="No projects found"
                data-testid="share-link-project-client"
              />
            </div>
            <div>
              <Label className="text-xs">Client</Label>
              <SearchableCombobox
                options={partyOptions}
                value={partyId}
                onValueChange={setPartyId}
                placeholder={selectedProject?.client_name ? `Default: ${selectedProject.client_name}` : 'Select client'}
                searchPlaceholder="Search clients..."
                emptyText="No clients found"
                data-testid="share-link-client"
              />
              {!partyId && selectedProject?.client_name && (
                <p className="text-[10px] text-stone-500 mt-1">Using project&apos;s attached client by default.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <Button
          type="button"
          onClick={handleGenerate}
          disabled={generating || !projectId}
          className="w-full bg-stone-900 hover:bg-stone-800 text-white"
          data-testid="share-link-generate-btn"
        >
          {generating ? (
            <>
              <RefreshCcw size={14} className="mr-1.5 animate-spin" /> Generating...
            </>
          ) : (
            <>
              <Link2 size={14} className="mr-1.5" /> {mode === 'vendor' ? 'Get Vendor Quote Link' : 'Get Client Status Link'}
            </>
          )}
        </Button>

        {result && (
          <div className="mt-2 space-y-3 border-t border-stone-200 pt-3" data-testid="share-link-result">
            {result.reused && (
              <div className="text-[11px] flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                <CheckCircle2 size={12} /> Reused existing active link
              </div>
            )}
            <div>
              <Label className="text-xs">Public link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={result.public_url || ''}
                  readOnly
                  className="text-xs font-mono"
                  data-testid="share-link-url"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleCopy} data-testid="share-link-copy">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
              {result.expiry_date && (
                <p className="text-[10px] text-stone-500 mt-1">Expires {result.expiry_date.slice(0, 10)}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Email via Resend</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="text-xs"
                  data-testid="share-link-email-input"
                />
                <Button
                  type="button"
                  onClick={handleEmail}
                  disabled={emailSending || !emailTo}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="share-link-email-btn"
                >
                  <Mail size={14} className="mr-1" /> {emailSending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
